import { useState } from "react";
import { Inbox, Send, Mail, MessageCircle, CheckCircle2, ChevronDown, ChevronUp, AlertCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const QUICK_TEMPLATES = [
  { l: "Merci — devis sous 48h", m: "Merci pour votre demande. Notre équipe étudie votre projet et vous enverra une proposition détaillée sous 48h. Pour toute question urgente, WhatsApp : +86 198 7800 0805." },
  { l: "Complément d'infos", m: "Merci pour votre message. Pour préparer le meilleur devis possible, pourriez-vous nous préciser : quantité approximative, budget et délai souhaité ? Merci d'avance." },
  { l: "Rendez-vous WhatsApp", m: "Merci pour votre intérêt. Je vous propose un point rapide sur WhatsApp au +86 198 7800 0805 pour approfondir votre projet. Quel moment vous conviendrait ?" },
];

function fmtDate(iso) { try { return new Date(iso).toLocaleString("fr-FR"); } catch { return iso; } }

export default function MessagesPanel({ messages, onReload }) {
  const [openId, setOpenId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [sending, setSending] = useState(null);
  const [err, setErr] = useState("");
  const [status, setStatus] = useState(null); // {id, email_sent}

  const setDraft = (id, v) => setDrafts((d) => ({ ...d, [id]: v }));

  const reply = async (m) => {
    const text = (drafts[m.id] || "").trim();
    if (!text) return setErr("Écrivez une réponse avant d'envoyer.");
    setSending(m.id); setErr("");
    try {
      const r = await api.post(`/admin/contact/${m.id}/reply`, { message: text });
      setStatus({ id: m.id, email_sent: r.data.email_sent });
      setDraft(m.id, "");
      onReload && onReload();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSending(null); }
  };

  if (!messages || messages.length === 0) {
    return <p className="text-[var(--ink-soft)]"><Inbox className="inline mr-2" size={16} /> Aucun message pour le moment.</p>;
  }

  return (
    <div className="space-y-3" data-testid="admin-messages">
      {err && <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded text-sm">{err}</div>}
      {messages.map((m) => {
        const isOpen = openId === m.id;
        const draft = drafts[m.id] ?? "";
        const replies = m.replies || [];
        const st = status && status.id === m.id ? status : null;
        return (
          <div key={m.id} className="card-flat p-5" data-testid={`admin-msg-${m.id}`}>
            <div className="flex justify-between items-start gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-bold">{m.subject}</p>
                  {m.replied && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={10}/> Répondu</span>}
                </div>
                <p className="text-xs text-[var(--ink-soft)] mt-1">
                  {m.name} · <a href={`mailto:${m.email}`} className="hover:text-[var(--accent)]" data-testid={`msg-email-${m.id}`}>{m.email}</a> · {fmtDate(m.created_at)}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject || "")}`} className="text-xs inline-flex items-center gap-1 hover:text-[var(--accent)]" title="Ouvrir dans votre boîte mail" data-testid={`msg-mailto-${m.id}`}>
                  <Mail size={13} /> Email
                </a>
                <button onClick={() => setOpenId(isOpen ? null : m.id)} className="btn-outline !py-1.5 !px-3 text-xs inline-flex items-center gap-1" data-testid={`msg-toggle-${m.id}`}>
                  {isOpen ? <><ChevronUp size={14}/> Fermer</> : <><MessageCircle size={14}/> Répondre</>}
                </button>
              </div>
            </div>
            <p className="mt-3 text-sm bg-[var(--bg-soft)] p-3 rounded whitespace-pre-wrap">{m.message}</p>

            {replies.length > 0 && (
              <div className="mt-4 space-y-2" data-testid={`msg-replies-${m.id}`}>
                <p className="text-xs font-bold uppercase tracking-widest text-[var(--ink-soft)]">Historique des réponses</p>
                {replies.map((r) => (
                  <div key={r.id} className="border-l-4 border-emerald-400 bg-emerald-50/50 px-4 py-2 rounded">
                    <p className="text-xs text-[var(--ink-soft)]">
                      <strong>{r.admin_name}</strong> · {fmtDate(r.sent_at)}
                      {r.email_sent
                        ? <span className="ml-2 inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 size={10}/> Email envoyé</span>
                        : <span className="ml-2 inline-flex items-center gap-1 text-amber-700"><AlertCircle size={10}/> Email non envoyé (Resend non configuré)</span>
                      }
                    </p>
                    <p className="text-sm mt-1 whitespace-pre-wrap">{r.message}</p>
                  </div>
                ))}
              </div>
            )}

            {isOpen && (
              <div className="mt-4 border-t border-black/5 pt-4 space-y-3" data-testid={`msg-reply-form-${m.id}`}>
                <div className="flex flex-wrap gap-2">
                  {QUICK_TEMPLATES.map((t, i) => (
                    <button key={i} type="button" onClick={() => setDraft(m.id, t.m)} className="text-xs bg-[var(--bg-soft)] hover:bg-[var(--accent)]/10 px-3 py-1.5 rounded-full" data-testid={`msg-template-${m.id}-${i}`}>
                      + {t.l}
                    </button>
                  ))}
                </div>
                <textarea rows={4} value={draft} onChange={(e) => setDraft(m.id, e.target.value)}
                  placeholder={`Répondre à ${m.name}…`}
                  className="input-field w-full"
                  data-testid={`msg-reply-text-${m.id}`} />
                {st && (
                  <div className={`text-xs px-3 py-2 rounded ${st.email_sent ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`}>
                    {st.email_sent
                      ? "✓ Réponse enregistrée et email envoyé au client."
                      : "⚠ Réponse enregistrée. L'email automatique n'a pas pu être envoyé (Resend non configuré) — utilisez le bouton \"Email\" ci-dessus ou WhatsApp pour recontacter."}
                  </div>
                )}
                <div className="flex items-center justify-between gap-3">
                  <p className="text-[10px] text-[var(--ink-soft)]">La réponse est envoyée automatiquement à <strong>{m.email}</strong> et archivée ci-dessus.</p>
                  <button disabled={sending === m.id || !draft.trim()} onClick={() => reply(m)} className="btn-primary !py-2 !px-4 text-sm disabled:opacity-50" data-testid={`msg-send-${m.id}`}>
                    <Send size={14}/> {sending === m.id ? "Envoi…" : "Envoyer la réponse"}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
